// System-prompt cascade data layer (epic #635, #637). Three optional prose
// layers that SPECIALIZE developerz.ai's built-in agent instructions (they
// never replace them): ORG (account-wide), REPO (per repository), USER (the
// acting user's own dispatched work). Resolution order: platform → org → repo
// → user. Each layer reads/writes over `apiFetch` with the session bearer,
// scoped to the active account. Same null-or-row envelope shape everywhere.
//
// Each layer is also keyed by ROLE (plan 101 slice 06): `?role=` on all nine
// routes picks prose for one run type (`worker`, `reviewer`, …) instead of the
// all-roles row. Both apply at compose time, narrowest last. The choices are the
// domain's AUTHORABLE allowlist — `triage` is platform-only and never offered.

import { type AuthorablePromptRole, PROMPT_ROLE_ALL } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { activeAccountId } from './active-account';
import { apiFetch } from './api';
import { SESSION_TOKEN } from './session';

/** Server-enforced text ceiling — mirrored here for the char counter + guard. */
export const SYSTEM_PROMPT_MAX = 20000;

export type PromptScope = 'org' | 'repo' | 'user';

/** One stored layer, or `null` when the member hasn't authored that scope yet. */
export type SystemPrompt = {
  readonly scope: PromptScope;
  readonly scope_ref: string | null;
  readonly text: string;
  readonly updated_at_iso: string;
};

type PromptEnvelope = { readonly prompt: SystemPrompt | null };

/** Which run a layer's prose is for: an authorable role, or `''` for every run. */
export type PromptRoleChoice = AuthorablePromptRole | typeof PROMPT_ROLE_ALL;

/** `path` addressed at one role's layer. The all-roles layer sends no param. */
function withRole(path: string, role: PromptRoleChoice): string {
  return role === PROMPT_ROLE_ALL ? path : `${path}?role=${encodeURIComponent(role)}`;
}

export const ORG_PROMPT_QUERY_KEY = 'system_prompt_org';
export const REPO_PROMPT_QUERY_KEY = 'system_prompt_repo';
export const MY_PROMPT_QUERY_KEY = 'system_prompt_me';

/**
 * The account-wide ORG layer. Idle with no session, like the org key pool.
 * `enabled` is a second, REACTIVE gate (default admits): the GET needs
 * `write:prompts` (`apps/api/src/routes/system-prompts.ts`), so a caller whose
 * `/v1/me` lacks it passes a verdict and the read never fires (#7426) — the
 * `useAgentModes` shape.
 */
export function useOrgPrompt(
  role: () => PromptRoleChoice,
  token: string | null = SESSION_TOKEN,
  enabled: () => boolean = () => true,
) {
  return createQuery<SystemPrompt | null>(() => ({
    queryKey: [ORG_PROMPT_QUERY_KEY, role(), token, activeAccountId()],
    enabled: token !== null && enabled(),
    queryFn: async () =>
      (await apiFetch<PromptEnvelope>(withRole('/v1/system-prompts/org', role()), token)).prompt,
  }));
}

/**
 * The per-repo REPO layer, keyed by repo id. Idle without a session or id, and
 * behind `enabled` for {@link useOrgPrompt}'s reason (same `write:prompts` GET).
 */
export function useRepoPrompt(
  repoId: () => string,
  role: () => PromptRoleChoice,
  token: string | null = SESSION_TOKEN,
  enabled: () => boolean = () => true,
) {
  return createQuery<SystemPrompt | null>(() => ({
    queryKey: [REPO_PROMPT_QUERY_KEY, repoId(), role(), token, activeAccountId()],
    enabled: token !== null && repoId() !== '' && enabled(),
    queryFn: async () =>
      (await apiFetch<PromptEnvelope>(withRole(repoPath(repoId()), role()), token)).prompt,
  }));
}

function repoPath(repoId: string): string {
  return `/v1/system-prompts/repo/${encodeURIComponent(repoId)}`;
}

/** The acting user's own USER layer (self). Idle with no session. */
export function useMyPrompt(role: () => PromptRoleChoice, token: string | null = SESSION_TOKEN) {
  return createQuery<SystemPrompt | null>(() => ({
    queryKey: [MY_PROMPT_QUERY_KEY, role(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: async () =>
      (await apiFetch<PromptEnvelope>(withRole('/v1/me/system-prompt', role()), token)).prompt,
  }));
}

async function putPrompt(path: string, text: string, token: string | null): Promise<SystemPrompt> {
  return (
    await apiFetch<PromptEnvelope & { prompt: SystemPrompt }>(path, token, {
      method: 'PUT',
      body: JSON.stringify({ text }),
    })
  ).prompt;
}

const deletePrompt = (path: string, token: string | null) =>
  apiFetch<void>(path, token, { method: 'DELETE' });

export const setOrgPrompt = (
  text: string,
  role: PromptRoleChoice,
  token: string | null = SESSION_TOKEN,
) => putPrompt(withRole('/v1/system-prompts/org', role), text, token);
export const clearOrgPrompt = (role: PromptRoleChoice, token: string | null = SESSION_TOKEN) =>
  deletePrompt(withRole('/v1/system-prompts/org', role), token);

export const setRepoPrompt = (
  repoId: string,
  text: string,
  role: PromptRoleChoice,
  token: string | null = SESSION_TOKEN,
) => putPrompt(withRole(repoPath(repoId), role), text, token);
export const clearRepoPrompt = (
  repoId: string,
  role: PromptRoleChoice,
  token: string | null = SESSION_TOKEN,
) => deletePrompt(withRole(repoPath(repoId), role), token);

export const setMyPrompt = (
  text: string,
  role: PromptRoleChoice,
  token: string | null = SESSION_TOKEN,
) => putPrompt(withRole('/v1/me/system-prompt', role), text, token);
export const clearMyPrompt = (role: PromptRoleChoice, token: string | null = SESSION_TOKEN) =>
  deletePrompt(withRole('/v1/me/system-prompt', role), token);
